/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import * as React from "react";

import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation, Pagination, Autoplay, FreeMode } from "swiper/modules";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

import { siguienteIntento } from "@/lib/foto-de-ficha";

// Import Swiper styles


import "swiper/css";

import "swiper/css/navigation";

import "swiper/css/pagination";

import "swiper/css/free-mode";

interface CarouselProps {

  images: string[];

  slug?: string;

  locale?: string;

  slidesPerView?: number;

  className?: string;

  showNavigation?: boolean;

  showPagination?: boolean;

  autoplay?: boolean;

  autoplayDelay?: number;

  onSlideClick?: (index: number) => void;

  onSlideChange?: (index: number) => void;

  height?: string;

  spaceBetween?: number;

  slidesPerGroup?: number;

  navigationSize?: "sm" | "md" | "lg";

  objectFit?: "cover" | "contain";

  freeMode?: boolean;

  showCounter?: boolean;

  controlledIndex?: number;

}

interface CarouselNormalProps {

  images: string[];

  slug?: string;

  locale?: string;

  slidesPerView?: number;

  className?: string;

}

export const Carousel: React.FC<CarouselProps> = ({

  images,

  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  slug: _slug,

  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  locale: _locale,

  slidesPerView = 1,

  className,

  showNavigation = true,

  showPagination = true,

  autoplay = false,

  autoplayDelay = 3000,

  onSlideClick,

  onSlideChange,

  height = "h-48 sm:h-64",

  spaceBetween = 0,

  slidesPerGroup,

  navigationSize = "md",

  objectFit = "cover",

  freeMode = false,

  showCounter = false,

  controlledIndex,

}) => {

  const swiperRef = React.useRef<any>(null);

  const [currentIndex, setCurrentIndex] = React.useState(0);

  // Sync carousel when controlledIndex changes

  React.useEffect(() => {

    if (controlledIndex !== undefined && swiperRef.current) {

      const currentIndex = swiperRef.current.activeIndex;

      if (currentIndex !== controlledIndex) {

        swiperRef.current.slideTo(controlledIndex);

      }

    }

  }, [controlledIndex]);

  const handlePrev = (e: React.MouseEvent) => {

    e.stopPropagation();

    if (swiperRef.current) {

      swiperRef.current.slidePrev();

    }

  };

  const handleNext = (e: React.MouseEvent) => {

    e.stopPropagation();

    if (swiperRef.current) {

      swiperRef.current.slideNext();

    }

  };

  const handleSlideClick = (index: number) => {

    if (onSlideClick) {

      onSlideClick(index);

    }

  };

  if (!images || images.length === 0) {

    return (

      <div className={cn("w-full h-48 bg-gray-200 rounded-md flex items-center justify-center", className)}>

        <span className="text-gray-500">No images available</span>

      </div>

    );

  }

  const isAutoHeight = height.includes("h-auto");

  

  return (

    <div className={cn("relative w-full flex items-center justify-center", className)}>

      <Swiper

        modules={freeMode ? [Navigation, Pagination, Autoplay, FreeMode] : [Navigation, Pagination, Autoplay]}

        spaceBetween={spaceBetween}

        slidesPerView={slidesPerView}

        slidesPerGroup={slidesPerGroup}

        freeMode={freeMode}

        breakpoints={

          slidesPerView > 1

            ? {

                320: { 

                  slidesPerView: Math.min(2, slidesPerView), 

                  spaceBetween: spaceBetween,

                  slidesPerGroup: slidesPerGroup ? Math.min(2, slidesPerGroup) : undefined,

                },

                640: { 

                  slidesPerView: Math.min(4, slidesPerView), 

                  spaceBetween: spaceBetween,

                  slidesPerGroup: slidesPerGroup ? Math.min(4, slidesPerGroup) : undefined,

                },

                1024: { 

                  slidesPerView: slidesPerView, 

                  spaceBetween: spaceBetween,

                  slidesPerGroup: slidesPerGroup,

                },

              }

            : undefined

        }

        navigation={false}

        pagination={showPagination ? { clickable: true } : false}

        autoplay={autoplay ? { delay: autoplayDelay, disableOnInteraction: false } : false}

        onSwiper={(swiper) => {

          swiperRef.current = swiper;

        }}

        onSlideChange={(swiper) => {

          setCurrentIndex(swiper.activeIndex);

          if (onSlideChange) {

            onSlideChange(swiper.activeIndex);

          }

        }}

        className={cn("w-full", isAutoHeight ? "h-auto" : "h-full")}

      >

        {images.map((image, index) => (

          <SwiperSlide key={index} className={isAutoHeight ? "!h-auto" : ""}>

            <div 

              className={cn(

                "relative w-full flex items-center justify-center", 

                isAutoHeight ? "h-auto" : height,

                onSlideClick && "cursor-pointer hover:opacity-90 transition-opacity rounded-md overflow-hidden"

              )}

              onClick={() => handleSlideClick(index)}

            >

              <img

                src={image}

                alt={`Property image ${index + 1}`}

                className={cn(

                  isAutoHeight && objectFit === "contain"

                    ? "h-auto w-auto max-w-full max-h-full object-contain"

                    : objectFit === "contain" 

                      ? "h-full w-auto max-w-full object-contain" 

                      : "w-full h-full object-cover"

                )}

                loading="lazy"

                referrerPolicy="no-referrer"

                onError={(e) => {

                  const siguiente = siguienteIntento(e.currentTarget.src);

                  if (siguiente) e.currentTarget.src = siguiente;

                }}

              />

            </div>

          </SwiperSlide>

        ))}

      </Swiper>

      {showCounter && images.length > 0 && (

        <div className="absolute top-2 left-2 z-20 bg-black/30 text-white px-2 py-1 rounded text-sm">

          {currentIndex + 1}/{images.length}

        </div>

      )}

      {showNavigation && images.length > 1 && (

        <>

          <button

            onClick={handlePrev}

            className={cn(

              "absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors",

              navigationSize === "sm" && "p-1",

              navigationSize === "md" && "p-2",

              navigationSize === "lg" && "p-3"

            )}

            aria-label="Previous image"

          >

            <ChevronLeft className={cn(

              navigationSize === "sm" && "h-3 w-3",

              navigationSize === "md" && "h-4 w-4",

              navigationSize === "lg" && "h-5 w-5"

            )} />

          </button>

          <button

            onClick={handleNext}

            className={cn(

              "absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors",

              navigationSize === "sm" && "p-1",

              navigationSize === "md" && "p-2",

              navigationSize === "lg" && "p-3"

            )}

            aria-label="Next image"

          >

            <ChevronRight className={cn(

              navigationSize === "sm" && "h-3 w-3",

              navigationSize === "md" && "h-4 w-4",

              navigationSize === "lg" && "h-5 w-5"

            )} />

          </button>

        </>

      )}

    </div>

  );

};

export const CarouselNormal: React.FC<CarouselNormalProps> = ({

  images,

  slug,

  locale,

  slidesPerView = 1,

  className,

}) => {

  return (

    <Carousel

      images={images}

      slug={slug}

      locale={locale}

      slidesPerView={slidesPerView}

      className={className}

      showNavigation={true}

      showPagination={true}

      autoplay={false}

    />

  );

};

